/**
 * Ligne de sujet forum — même présentation que la page Forums
 * (non-lus, épinglés, fermés, auteurs récents, aperçu, badge réponses).
 */

import { memo, type ReactNode } from 'react';
import { PlayerAvatar } from '../PlayerAvatar';
import { formatRelativeDate, formatShortDate, formatFullDate } from '../../utils/forumDates';

export interface ForumThreadListItem {
  id: number;
  thread: number;
  author: string;
  authorDisplay: string;
  subject: string;
  date: string;
  replyCount: number;
  lastReply: string | null;
  lastReplyAuthor?: string | null;
  lastReplyAuthorDisplay?: string | null;
  recentAuthors?: { pseudo: string; display: string }[];
  preview?: string;
  sticky?: boolean;
  closed?: boolean;
  unread?: boolean | null;
  unreadCount?: number | null;
}

function threadParticipants(thread: ForumThreadListItem): { pseudo: string; display: string }[] {
  if (thread.recentAuthors && thread.recentAuthors.length > 0) {
    return thread.recentAuthors;
  }
  const list: { pseudo: string; display: string }[] = [
    { pseudo: thread.author, display: thread.authorDisplay },
  ];
  const last = thread.lastReplyAuthor;
  if (
    last
    && thread.replyCount > 0
    && last.toLowerCase() !== thread.author.toLowerCase()
  ) {
    list.push({
      pseudo: last,
      display: thread.lastReplyAuthorDisplay || last,
    });
  }
  return list;
}

export const ForumThreadItem = memo(function ForumThreadItem({
  thread,
  onClick,
  isActive = false,
  compact = false,
  forumTag,
  isRecentsNew = false,
  isEnter = false,
}: {
  thread: ForumThreadListItem;
  onClick: () => void;
  isActive?: boolean;
  compact?: boolean;
  /** Chip forum (ex. page Récents) — déjà rendu. */
  forumTag?: ReactNode;
  isRecentsNew?: boolean;
  isEnter?: boolean;
}) {
  const hasLastReply = Boolean(thread.lastReply && thread.lastReply !== thread.date);
  const activityDate = hasLastReply ? thread.lastReply! : thread.date;
  const participants = threadParticipants(thread);
  const maxShown = compact ? 2 : 4;
  const shown = participants.slice(0, maxShown);
  const hasMoreAuthors = participants.length > maxShown
    || (Boolean(thread.recentAuthors) && thread.recentAuthors!.length >= 4 && thread.replyCount >= 3);
  const preview = (thread.preview || '').trim();
  const isUnread = thread.unread === true;
  const miniSize = compact ? 14 : 16;

  return (
    <button
      type="button"
      className={`forums-thread-item forums-thread-item--inline-authors${isActive ? ' active' : ''}${thread.sticky ? ' sticky' : ''}${thread.closed ? ' closed' : ''}${compact ? ' compact-item' : ''}${isUnread ? ' unread' : ''}${isRecentsNew ? ' recents-new' : ''}${isEnter ? ' forums-thread-item--enter' : ''}`}
      onClick={onClick}
      aria-pressed={isActive}
    >
      <div className="forums-thread-info">
        <div className="forums-thread-subject">
          {isRecentsNew && (
            <span className="forums-recents-new-pill" title="Nouveau depuis votre dernier passage sur Récents">
              nouveau
            </span>
          )}
          {!isRecentsNew && isUnread && (
            <span className="forums-unread-dot" title="Non lu" aria-label="Non lu" />
          )}
          {thread.sticky && <span className="forums-pin-badge" title="Épinglé">📌</span>}
          {thread.closed && <span className="forums-lock-badge" title="Fermé">🔒</span>}
          <span className="forums-thread-subject-text">{thread.subject}</span>
        </div>
        <div className="forums-thread-meta">
          {forumTag && (
            <>
              {forumTag}
              <span className="forums-thread-sep">·</span>
            </>
          )}
          <span className="forums-thread-participants">
            {shown.map((p, i) => (
              <span key={`${p.pseudo}-${i}`} className="forums-thread-participant" title={p.display}>
                <PlayerAvatar
                  pseudo={p.pseudo}
                  size={miniSize}
                  className="forums-thread-participant-avatar"
                />
                <span className="forums-thread-participant-name">{p.display}</span>
              </span>
            ))}
            {hasMoreAuthors && <span className="forums-thread-more-authors" aria-hidden>…</span>}
          </span>
          {compact && (
            <>
              <span className="forums-thread-sep">·</span>
              <span className="forums-thread-date" title={formatFullDate(activityDate)}>
                {formatRelativeDate(activityDate)}
              </span>
              {thread.replyCount > 0 && (
                <>
                  <span className="forums-thread-sep">·</span>
                  <span className="forums-thread-replies">{thread.replyCount} rép.</span>
                </>
              )}
            </>
          )}
        </div>
        <div className={`forums-thread-preview${preview ? '' : ' is-empty'}`}>
          {preview || '\u00A0'}
        </div>
      </div>
      {!compact && (
        <div className="forums-thread-end">
          <span className="forums-thread-time" title={formatFullDate(activityDate)}>
            {formatShortDate(activityDate)}
          </span>
          {isUnread && (thread.unreadCount ?? 0) > 0 ? (
            <span
              className="forums-thread-reply-badge forums-thread-unread-badge"
              title={`${thread.unreadCount} non lu${(thread.unreadCount ?? 0) > 1 ? 's' : ''}`}
            >
              {thread.unreadCount}
            </span>
          ) : thread.replyCount > 0 ? (
            <span
              className="forums-thread-reply-badge"
              title={`${thread.replyCount} réponse${thread.replyCount > 1 ? 's' : ''}`}
            >
              {thread.replyCount}
            </span>
          ) : (
            <span className="forums-thread-activity-label">
              {hasLastReply ? 'activité' : 'nouveau'}
            </span>
          )}
        </div>
      )}
    </button>
  );
});
